// App.js
import React, { Suspense, useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline, Box, createTheme } from "@mui/material";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { useSelector } from "react-redux"; // Import useSelector

// Pages
import TermsOfService from "./pages/terms-of-service";
import PrivacyPolicy from "./pages/privacy-policy";
import HomeV3 from "./pages/home/homev3/index.tsx";
import AboutUs from "./pages/about-us";
import Admin from "./pages/admin";
import Resources from "./pages/resources";
import Features from "./pages/features";
import Partners from "./pages/partners";
import Dieticians from "./pages/dieticians";
import Trainers from "./pages/trainers";
import { GymOwnerTools } from "./pages/gym-owner-tools";
import Onboarding from "./pages/onboarding";
// UI
import Navbar from "./components/nav-bar";
import PageTransition from "./components/page-transition";
import Loading from "./components/loading";
import getTheme from "./theme"; // Import getTheme function

/** Inner app: lives inside <Router/> so we can use useLocation() */
function AppInner() {
  const location = useLocation();

  useEffect(() => {
    AOS.init();
  }, []);

  // Hide the navbar on /gym-owner-tools and any sub-paths
  const hideNav = location.pathname.startsWith("/partners");

  return (
    <>
      {/* Route transition indicator */}
      <PageTransition />

      {/* Navbar with intro slide-in animation; not rendered on gym-owner-tools */}
      {!hideNav && (
        <motion.div
          initial={{ y: -64, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          data-aos="false"
        >
          <Navbar />
        </motion.div>
      )}

      {/* Routes */}
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<HomeV3 />} />
          <Route path="/dieticians" element={<Dieticians />} />
          <Route path="/trainers" element={<Trainers />} />
          <Route path="/TOS" element={<TermsOfService />} />
          <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="/AboutUs" element={<AboutUs />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/features" element={<Features />} />
          <Route path="/gym-owner-tools" element={<GymOwnerTools />} />
          <Route path="/onboarding" element={<Onboarding />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  const darkMode = useSelector((state) => state.partners.darkMode);

  const theme = useMemo(() => {
    const rawTheme = getTheme(darkMode ? "dark" : "light");
    return createTheme(rawTheme);
  }, [darkMode]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
        <CssBaseline />
        <Router>
          <AppInner />
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;

